import { useTranslation } from '@firmable/i18n';
import clsx from 'clsx';
import React from 'react';

interface TextAreaProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> {
  size?: 'lg' | 'sm';
  label?: string;
  labelOptional?: boolean;
  assistiveText?: string;
  error?: string;
  containerClassName?: string;
  textAreaClassName?: string;
}

const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      size = 'lg',
      label,
      labelOptional,
      assistiveText,
      error,
      containerClassName,
      textAreaClassName,
      disabled,
      id,
      ...rest
    },
    ref,
  ) => {
    const { t } = useTranslation('common');
    const isLg = size === 'lg';
    const hasError = !!error;

    return (
      <div className={clsx('flex flex-col gap-xs', containerClassName)}>
        {label ? (
          <div className="flex items-center justify-between gap-sm">
            <label htmlFor={id} className="text-label-small text-content-dark">
              {label}
            </label>
            {labelOptional ? <span className="text-label-small text-content-light">{t('optionalLabel')}</span> : null}
          </div>
        ) : null}

        <textarea
          ref={ref}
          id={id}
          disabled={disabled}
          className={clsx(
            'w-full border border-solid resize-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stroke-interactive',
            {
              'p-sm rounded-radius-md text-body-large': isLg,
              'px-sm py-xs rounded-radius-sm text-body-small': !isLg,
            },
            'text-content-dark placeholder:text-content-light placeholder:font-normal',
            {
              'bg-surface-lighter border-stroke-light cursor-not-allowed opacity-50': disabled,
              'bg-surface-white border-danger-default': !disabled && hasError,
              'bg-surface-white border-stroke-light hover:border-stroke-dark focus-visible:border-stroke-interactive':
                !disabled && !hasError,
            },
            textAreaClassName,
          )}
          aria-autocomplete="none"
          spellCheck
          {...rest}
        />

        {hasError || assistiveText ? (
          <p className={clsx('text-body-small', { 'text-danger-dark': hasError, 'text-content-light': !hasError })}>
            {hasError ? error : assistiveText}
          </p>
        ) : null}
      </div>
    );
  },
);

TextArea.displayName = 'V2TextArea';

export { TextArea };
export type { TextAreaProps as V2TextAreaProps };
